import { useMemo, useState } from "react";
import { mergeClasses, Tooltip } from "@fluentui/react-components";
import { DeliverableStatus } from "models/IProjectIQWorkspace";
import {
  MilestoneStatus,
  RaidCategory,
  RaidSeverity,
  RaidStatus,
} from "apps/project-iq/components/WorkspacePlan/WorkspacePlan.types";
import {
  computeScheduleProgress,
  countBy,
  formatDate,
} from "apps/project-iq/utils/workspaceOverviewUtils";
import type { CSSProperties } from "react";
import { buildAccentPalette, withAlpha } from "utils/accentPalette";
import {
  useClientHeroStyles,
  useStyles,
} from "./ClientWorkspaceOverview.styles";
import { IClientWorkspaceOverviewProps } from "./ClientWorkspaceOverview.types";
import {
  DEFAULT_RAG_DIMENSIONS,
  DELIVERABLE_STATUS_LABELS,
  MILESTONE_STATUS_LABELS,
  RAG_STATUS_COLOR,
  RAID_CATEGORY_LABELS,
  RAID_SEVERITY_LABELS,
  RAID_STATUS_LABELS,
} from "./ClientWorkspaceOverview.constants";

const HERO_KICKER_TEXT_ALPHA = 0.6;
const HERO_DESCRIPTION_TEXT_ALPHA = 0.82;

export const ClientWorkspaceOverview = ({
  workspace,
}: IClientWorkspaceOverviewProps): JSX.Element => {
  const styles = useStyles();
  const heroStyles = useClientHeroStyles();

  const milestones = workspace.projectPlan?.milestones ?? [];
  const deliverables = workspace.deliverables ?? [];
  const raidEntries = workspace.projectPlan?.raidEntries ?? [];

  const accentPalette = useMemo(
    () =>
      workspace.customBrandingEnabled
        ? buildAccentPalette(workspace.accentColor, workspace.headerColor)
        : undefined,
    [
      workspace.customBrandingEnabled,
      workspace.accentColor,
      workspace.headerColor,
    ],
  );

  const heroCssVariables = useMemo<CSSProperties | undefined>(
    () =>
      accentPalette
        ? ({
            "--hero-banner-bg": accentPalette.headerBg,
            "--hero-kicker-text": withAlpha(
              accentPalette.headerText,
              HERO_KICKER_TEXT_ALPHA,
            ),
            "--hero-title-text": accentPalette.headerText,
            "--hero-description-text": withAlpha(
              accentPalette.headerText,
              HERO_DESCRIPTION_TEXT_ALPHA,
            ),
            "--hero-progress-fill": accentPalette.accentOnWhite,
          } as CSSProperties)
        : undefined,
    [accentPalette],
  );

  const scheduleProgress = useMemo(() => {
    const schedule = workspace.projectPlan?.ragDimensions?.find(
      (d) => d.id === "schedule",
    );
    if (!schedule?.startDate || !schedule?.endDate) return null;
    return computeScheduleProgress(schedule.startDate, schedule.endDate);
  }, [workspace.projectPlan?.ragDimensions]);

  const milestoneCounts = useMemo(
    () => countBy(milestones, (m) => m.status),
    [milestones],
  );
  const deliverableCounts = useMemo(
    () => countBy(deliverables, (d) => d.status),
    [deliverables],
  );
  const raidByCategory = useMemo(
    () => countBy(raidEntries, (r) => r.category),
    [raidEntries],
  );
  const raidBySeverity = useMemo(
    () => countBy(raidEntries, (r) => r.severity),
    [raidEntries],
  );
  const raidByStatus = useMemo(
    () => countBy(raidEntries, (r) => r.status),
    [raidEntries],
  );

  const sortedMilestones = useMemo(
    () =>
      [...milestones].sort((a, b) => {
        const aTime = a.dueDate ? new Date(a.dueDate).getTime() : 0;
        const bTime = b.dueDate ? new Date(b.dueDate).getTime() : 0;
        return bTime - aTime;
      }),
    [milestones],
  );
  const sortedDeliverables = useMemo(
    () => [...deliverables].sort((a, b) => b.updatedAt - a.updatedAt),
    [deliverables],
  );
  const sortedRaidEntries = useMemo(
    () =>
      [...raidEntries].sort((a, b) => {
        const aTime = a.dueDate ? new Date(a.dueDate).getTime() : 0;
        const bTime = b.dueDate ? new Date(b.dueDate).getTime() : 0;
        return bTime - aTime;
      }),
    [raidEntries],
  );

  const [milestoneStatusFilter, setMilestoneStatusFilter] = useState<
    string | null
  >(null);
  const [deliverableStatusFilter, setDeliverableStatusFilter] = useState<
    string | null
  >(null);
  const [raidStatusFilter, setRaidStatusFilter] = useState<string | null>(null);
  const [raidCategoryFilter, setRaidCategoryFilter] = useState<string | null>(
    null,
  );
  const [raidSeverityFilter, setRaidSeverityFilter] = useState<string | null>(
    null,
  );

  const filteredMilestones = useMemo(
    () =>
      milestoneStatusFilter
        ? sortedMilestones.filter((m) => m.status === milestoneStatusFilter)
        : sortedMilestones,
    [sortedMilestones, milestoneStatusFilter],
  );
  const filteredDeliverables = useMemo(
    () =>
      deliverableStatusFilter
        ? sortedDeliverables.filter((d) => d.status === deliverableStatusFilter)
        : sortedDeliverables,
    [sortedDeliverables, deliverableStatusFilter],
  );
  const filteredRaidEntries = useMemo(
    () =>
      sortedRaidEntries.filter(
        (r) =>
          (!raidCategoryFilter || r.category === raidCategoryFilter) &&
          (!raidSeverityFilter || r.severity === raidSeverityFilter) &&
          (!raidStatusFilter || r.status === raidStatusFilter),
      ),
    [
      sortedRaidEntries,
      raidCategoryFilter,
      raidSeverityFilter,
      raidStatusFilter,
    ],
  );

  const milestonePillClass: Record<MilestoneStatus, string> = {
    not_started: styles.pillUpcoming,
    in_progress: styles.pillProgress,
    done: styles.pillComplete,
  };

  const deliverablePillClass: Record<DeliverableStatus, string> = {
    not_started: styles.pillUpcoming,
    in_progress: styles.pillProgress,
    done: styles.pillComplete,
  };

  const raidPillClass: Record<RaidSeverity, string> = {
    high: styles.pillHigh,
    medium: styles.pillMedium,
    low: styles.pillLow,
  };

  const raidStatusPillClass: Record<RaidStatus, string> = {
    not_started: styles.pillUpcoming,
    in_progress: styles.pillProgress,
    closed: styles.pillComplete,
  };

  const raidTypeBadgeClass: Record<RaidCategory, string> = {
    risk: styles.raidTypeRisk,
    action: styles.raidTypeAction,
    issue: styles.raidTypeIssue,
    decision: styles.raidTypeDecision,
  };

  // Do not add a margin/opportunity section here — out of scope per US-8.1; it exists only as a "Coming soon" mockup on Internal View.
  return (
    <div className={styles.root}>
      <div className={styles.body}>
        <div className={styles.content}>
          <section className={heroStyles.heroCard} style={heroCssVariables}>
            <div className={heroStyles.heroBanner}>
              <div className={heroStyles.heroKicker}>
                {[workspace.clientName, workspace.code]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
              <h2 className={heroStyles.heroBannerTitle}>{workspace.name}</h2>
              {workspace.description && (
                <p className={heroStyles.heroBannerDescription}>
                  {workspace.description}
                </p>
              )}
            </div>

            {(workspace.status || scheduleProgress) && (
              <div className={heroStyles.heroProgressSection}>
                <div className={heroStyles.heroProgressLabelRow}>
                  <span className={heroStyles.heroPhaseLabel}>
                    {workspace.status ?? "In delivery"}
                  </span>
                  {scheduleProgress && (
                    <span className={heroStyles.heroProgressText}>
                      {scheduleProgress.label} · {scheduleProgress.percent}%
                    </span>
                  )}
                </div>
                {scheduleProgress && (
                  <div className={styles.progressTrack}>
                    <div
                      className={styles.progressFill}
                      style={{ width: `${scheduleProgress.percent}%` }}
                    />
                  </div>
                )}
              </div>
            )}
          </section>

          <div className={styles.sectionTitleRow}>
            <span className={styles.sectionTitle}>Engagement Plan</span>
          </div>
          <div className={styles.statusGrid}>
            {(
              workspace.projectPlan?.ragDimensions ?? DEFAULT_RAG_DIMENSIONS
            ).map((dimension) => (
              <div key={dimension.id} className={styles.statusTile}>
                <span className={styles.statusLabel}>{dimension.label}</span>
                <div className={styles.statusValueRow}>
                  <span
                    className={styles.statusDot}
                    style={{
                      backgroundColor: RAG_STATUS_COLOR[dimension.status],
                    }}
                  />
                  <span className={styles.statusValue}>
                    {dimension.statusText}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className={styles.twoCol}>
            {/* Milestones */}
            <section className={mergeClasses(styles.card, styles.listCard)}>
              <div className={styles.cardHeaderRow}>
                <span className={styles.cardTitle}>Milestones</span>
                {(
                  Object.entries(MILESTONE_STATUS_LABELS) as [
                    MilestoneStatus,
                    string,
                  ][]
                )
                  .filter(([status]) => (milestoneCounts[status] ?? 0) > 0)
                  .map(([status, label]) => (
                    <span
                      key={status}
                      role="button"
                      tabIndex={0}
                      aria-pressed={milestoneStatusFilter === status}
                      aria-label={`Filter by ${MILESTONE_STATUS_LABELS[status]}`}
                      className={mergeClasses(
                        styles.counterChip,
                        styles.counterChipClickable,
                        milestonePillClass[status],
                        milestoneStatusFilter === status &&
                          styles.counterChipActive,
                      )}
                      onClick={() =>
                        setMilestoneStatusFilter((prev) =>
                          prev === status ? null : status,
                        )
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setMilestoneStatusFilter((prev) =>
                            prev === status ? null : status,
                          );
                        }
                      }}
                    >
                      {milestoneCounts[status]} {label}
                    </span>
                  ))}
              </div>
              {filteredMilestones.length > 0 ? (
                <div
                  className={styles.scrollableList}
                  role="list"
                  aria-label="Milestones"
                  tabIndex={0}
                >
                  {filteredMilestones.map((milestone) => (
                    <div
                      key={milestone.id}
                      className={styles.milestoneRow}
                      role="listitem"
                    >
                      <div className={styles.milestoneTopRow}>
                        <div className={styles.milestoneTitleLine}>
                          <Tooltip
                            content={milestone.name}
                            relationship="label"
                          >
                            <span className={styles.milestoneName}>
                              {milestone.name}
                            </span>
                          </Tooltip>
                          {milestone.description && (
                            <span className={styles.itemDescription}>
                              {" – "}
                              {milestone.description}
                            </span>
                          )}
                        </div>
                        <span
                          className={mergeClasses(
                            styles.pill,
                            milestonePillClass[milestone.status],
                          )}
                        >
                          {MILESTONE_STATUS_LABELS[milestone.status]}
                        </span>
                      </div>
                      {milestone.dueDate && (
                        <span className={styles.milestoneDue}>
                          Due {formatDate(milestone.dueDate)}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ) : milestones.length > 0 ? (
                <p className={styles.emptyHint}>
                  No milestones match the selected filter.
                </p>
              ) : (
                <p className={styles.emptyHint}>No milestones yet</p>
              )}
            </section>

            {/* Deliverables */}
            <section className={mergeClasses(styles.card, styles.listCard)}>
              <div className={styles.cardHeaderRow}>
                <span className={styles.cardTitle}>Deliverables</span>
                {(
                  Object.entries(DELIVERABLE_STATUS_LABELS) as [
                    DeliverableStatus,
                    string,
                  ][]
                )
                  .filter(([status]) => (deliverableCounts[status] ?? 0) > 0)
                  .map(([status, label]) => (
                    <span
                      key={status}
                      role="button"
                      tabIndex={0}
                      aria-pressed={deliverableStatusFilter === status}
                      aria-label={`Filter by ${DELIVERABLE_STATUS_LABELS[status]}`}
                      className={mergeClasses(
                        styles.counterChip,
                        styles.counterChipClickable,
                        deliverablePillClass[status],
                        deliverableStatusFilter === status &&
                          styles.counterChipActive,
                      )}
                      onClick={() =>
                        setDeliverableStatusFilter((prev) =>
                          prev === status ? null : status,
                        )
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setDeliverableStatusFilter((prev) =>
                            prev === status ? null : status,
                          );
                        }
                      }}
                    >
                      {deliverableCounts[status]} {label}
                    </span>
                  ))}
              </div>
              {filteredDeliverables.length > 0 ? (
                <div
                  className={styles.scrollableList}
                  role="list"
                  aria-label="Deliverables"
                  tabIndex={0}
                >
                  {filteredDeliverables.map((deliverable) => (
                    <div
                      key={deliverable.id}
                      className={styles.milestoneRow}
                      role="listitem"
                    >
                      <div className={styles.milestoneTopRow}>
                        <div className={styles.milestoneTitleLine}>
                          <Tooltip
                            content={deliverable.name}
                            relationship="label"
                          >
                            <span className={styles.milestoneName}>
                              {deliverable.name}
                            </span>
                          </Tooltip>
                          {deliverable.description && (
                            <span className={styles.itemDescription}>
                              {" – "}
                              {deliverable.description}
                            </span>
                          )}
                        </div>
                        <span
                          className={mergeClasses(
                            styles.pill,
                            deliverablePillClass[deliverable.status],
                          )}
                        >
                          {DELIVERABLE_STATUS_LABELS[deliverable.status]}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : deliverables.length > 0 ? (
                <p className={styles.emptyHint}>
                  No deliverables match the selected filter.
                </p>
              ) : (
                <p className={styles.emptyHint}>No deliverables yet</p>
              )}
            </section>

            {/* RAID Log */}
            <section className={mergeClasses(styles.card, styles.listCard)}>
              <div className={styles.cardHeaderRow}>
                <span className={styles.cardTitle}>RAID Log</span>
                {(
                  Object.entries(RAID_CATEGORY_LABELS) as [
                    RaidCategory,
                    string,
                  ][]
                )
                  .filter(([category]) => (raidByCategory[category] ?? 0) > 0)
                  .map(([category, label]) => (
                    <span
                      key={category}
                      role="button"
                      tabIndex={0}
                      aria-pressed={raidCategoryFilter === category}
                      aria-label={`Filter by ${label}`}
                      className={mergeClasses(
                        styles.counterChip,
                        styles.counterChipClickable,
                        raidTypeBadgeClass[category],
                        raidCategoryFilter === category &&
                          styles.counterChipActive,
                      )}
                      onClick={() =>
                        setRaidCategoryFilter((prev) =>
                          prev === category ? null : category,
                        )
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setRaidCategoryFilter((prev) =>
                            prev === category ? null : category,
                          );
                        }
                      }}
                    >
                      {raidByCategory[category]} {label}
                    </span>
                  ))}
                {(
                  Object.entries(RAID_SEVERITY_LABELS) as [
                    RaidSeverity,
                    string,
                  ][]
                )
                  .filter(([severity]) => (raidBySeverity[severity] ?? 0) > 0)
                  .map(([severity, label]) => (
                    <span
                      key={severity}
                      role="button"
                      tabIndex={0}
                      aria-pressed={raidSeverityFilter === severity}
                      aria-label={`Filter by ${label}`}
                      className={mergeClasses(
                        styles.counterChip,
                        styles.counterChipClickable,
                        raidPillClass[severity],
                        raidSeverityFilter === severity &&
                          styles.counterChipActive,
                      )}
                      onClick={() =>
                        setRaidSeverityFilter((prev) =>
                          prev === severity ? null : severity,
                        )
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setRaidSeverityFilter((prev) =>
                            prev === severity ? null : severity,
                          );
                        }
                      }}
                    >
                      {raidBySeverity[severity]} {label}
                    </span>
                  ))}
                {(Object.entries(RAID_STATUS_LABELS) as [RaidStatus, string][])
                  .filter(([status]) => (raidByStatus[status] ?? 0) > 0)
                  .map(([status, label]) => (
                    <span
                      key={status}
                      role="button"
                      tabIndex={0}
                      aria-pressed={raidStatusFilter === status}
                      aria-label={`Filter by status: ${RAID_STATUS_LABELS[status]}`}
                      className={mergeClasses(
                        styles.counterChip,
                        styles.counterChipClickable,
                        raidStatusPillClass[status],
                        raidStatusFilter === status && styles.counterChipActive,
                      )}
                      onClick={() =>
                        setRaidStatusFilter((prev) =>
                          prev === status ? null : status,
                        )
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setRaidStatusFilter((prev) =>
                            prev === status ? null : status,
                          );
                        }
                      }}
                    >
                      {raidByStatus[status]} {label}
                    </span>
                  ))}
              </div>
              {filteredRaidEntries.length > 0 ? (
                <div
                  className={styles.scrollableList}
                  role="list"
                  aria-label="RAID log entries"
                  tabIndex={0}
                >
                  {filteredRaidEntries.map((entry) => (
                    <div
                      key={entry.id}
                      className={styles.raidRow}
                      role="listitem"
                    >
                      <div className={styles.raidTopRow}>
                        <Tooltip content={entry.name} relationship="label">
                          <span className={styles.raidText}>{entry.name}</span>
                        </Tooltip>
                        <div className={styles.raidPillsRow}>
                          <span
                            className={mergeClasses(
                              styles.pill,
                              raidTypeBadgeClass[entry.category],
                            )}
                          >
                            {entry.category.toUpperCase()}
                          </span>
                          <span
                            className={mergeClasses(
                              styles.pill,
                              raidPillClass[entry.severity],
                            )}
                          >
                            {RAID_SEVERITY_LABELS[entry.severity]}
                          </span>
                          <span
                            className={mergeClasses(
                              styles.pill,
                              raidStatusPillClass[entry.status],
                            )}
                          >
                            {RAID_STATUS_LABELS[entry.status]}
                          </span>
                        </div>
                      </div>
                      {entry.dueDate && (
                        <span className={styles.raidMeta}>
                          Due {formatDate(entry.dueDate)}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ) : raidEntries.length > 0 ? (
                <p className={styles.emptyHint}>
                  No RAID entries match the selected filter.
                </p>
              ) : (
                <p className={styles.emptyHint}>No RAID entries yet</p>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};
